import React, { useState, useEffect } from 'react';
import {
    MdEmail, MdSave, MdSend, MdBusiness, MdLock, MdDownload, MdUpload,
    MdBackup, MdRestore, MdDelete, MdWarning, MdRefresh
} from 'react-icons/md';
import API from '../api';
import { useLanguage, translateUiText } from '../i18n';

const Section = ({ icon, title, children }) => (
    <div className="page-card mb-4">
        <div className="page-card-header">
            <h6 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{icon}{title}</h6>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
    </div>
);

const DATA_RESOURCES = ['buildings', 'spaces', 'assets', 'work_orders', 'maintenance_schedules', 'inventory_items', 'inventory_transactions', 'bookings', 'users'];
const IMPORT_RESOURCES = ['buildings', 'assets', 'inventory_items'];

const BACKUP_TYPE_META = {
    manual: { label: 'Manual', bg: '#dbeafe', color: '#1d4ed8' },
    auto: { label: 'Automatic', bg: '#dcfce7', color: '#15803d' },
    pre_restore: { label: 'Pre-restore', bg: '#fef9c3', color: '#b45309' },
};

function formatBytes(bytes) {
    if (!bytes) return '—';
    const units = ['B', 'KB', 'MB', 'GB'];
    let n = bytes, i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(1)} ${units[i]}`;
}

function BackupRestoreSection() {
    const { t } = useLanguage();
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [message, setMessage] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [confirmText, setConfirmText] = useState('');

    const load = async () => {
        setLoading(true);
        try { const { data } = await API.get('/backup'); setBackups(data); }
        catch (err) { setMessage(err.response?.data?.error || translateUiText('Could not load backups')); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const createBackup = async () => {
        setCreating(true); setMessage('');
        try {
            const { data } = await API.post('/backup');
            setMessage(data.message);
            load();
        } catch (err) { setMessage(err.response?.data?.error || translateUiText('Backup failed')); }
        finally { setCreating(false); }
    };

    const downloadBackup = async backup => {
        try {
            const response = await API.get(`/backup/${backup.id}/download`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a'); link.href = url; link.download = backup.filename; link.click(); URL.revokeObjectURL(url);
        } catch (err) { setMessage(err.response?.data?.error || translateUiText('Download failed')); }
    };

    const deleteBackup = async backup => {
        if (!window.confirm(translateUiText('Delete this backup? This cannot be undone.'))) return;
        setBusyId(backup.id);
        try { await API.delete(`/backup/${backup.id}`); load(); }
        catch (err) { setMessage(err.response?.data?.error || translateUiText('Delete failed')); }
        finally { setBusyId(null); }
    };

    const runRestore = async () => {
        if (!restoreTarget) return;
        setBusyId(restoreTarget.id); setMessage('');
        try {
            const { data } = await API.post(`/backup/${restoreTarget.id}/restore`, { confirm: confirmText });
            setMessage(data.message);
            setRestoreTarget(null); setConfirmText('');
            load();
        } catch (err) { setMessage(err.response?.data?.error || translateUiText('Restore failed')); }
        finally { setBusyId(null); }
    };

    return (
        <div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
                Back up the full database — every space, booking, user, asset and setting — and restore from a saved backup if something goes wrong. A safety backup is always taken automatically right before a restore, and one automatic backup is taken every day.
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-primary" onClick={createBackup} disabled={creating}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {creating ? <><span className="spinner-border spinner-border-sm me-2" />{t('settings.creatingBackup')}</> : <><MdBackup size={16} /> {t('settings.createBackupNow')}</>}
                </button>
                <button type="button" className="btn btn-light" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdRefresh size={16} /> {t('settings.refresh')}
                </button>
            </div>
            {message && <div className="alert alert-info py-2" style={{ fontSize: 13 }}>{message}</div>}

            {loading ? <div className="spinner-wrap"><div className="spinner-border" /></div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="fms-table">
                        <thead>
                            <tr><th>{t('settings.backupTableBackup')}</th><th>{t('settings.backupTableType')}</th><th>{t('settings.backupTableSize')}</th><th>{t('settings.backupTableCreated')}</th><th style={{ width: 210 }}>{t('settings.backupTableActions')}</th></tr>
                        </thead>
                        <tbody>
                            {backups.map(b => {
                                const meta = BACKUP_TYPE_META[b.backup_type] || { label: b.backup_type, bg: '#f1f5f9', color: '#475569' };
                                return (
                                    <tr key={b.id}>
                                        <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{b.filename}</td>
                                        <td>
                                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: meta.bg, color: meta.color }}>
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 12 }}>{formatBytes(b.size_bytes)}</td>
                                        <td style={{ fontSize: 12, color: '#94a3b8' }}>
                                            {new Date(b.created_at).toLocaleString()}{b.created_by_name ? ` · ${b.created_by_name}` : ''}
                                        </td>
                                        <td>
                                            {b.status === 'failed' ? (
                                                <span style={{ color: '#b91c1c', fontSize: 12 }} title={b.error_message}>{t('settings.backupFailed')}</span>
                                            ) : (
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button type="button" className="btn btn-sm btn-light" title={t('settings.download')} onClick={() => downloadBackup(b)}>
                                                        <MdDownload size={14} />
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-outline-warning" title={t('settings.restore')} disabled={busyId === b.id}
                                                        onClick={() => { setRestoreTarget(b); setConfirmText(''); }}>
                                                        <MdRestore size={14} /> {t('settings.restore')}
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-outline-danger" title={t('settings.delete')} disabled={busyId === b.id}
                                                        onClick={() => deleteBackup(b)}>
                                                        <MdDelete size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!backups.length && (
                                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>
                                    {t('settings.noBackups')}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {restoreTarget && (
                <div className="modal-overlay" onClick={() => setRestoreTarget(null)}>
                    <div className="modal-box" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: 20 }}>
                            <h6 style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#b91c1c', marginTop: 0 }}>
                                <MdWarning /> {t('settings.restoreModalTitle')}
                            </h6>
                            <p style={{ fontSize: 13, color: '#64748b' }}>
                                {t('settings.restoreModalBody', '', { filename: restoreTarget.filename, date: new Date(restoreTarget.created_at).toLocaleString() })}
                            </p>
                            <p style={{ fontSize: 13, marginBottom: 6 }}>{t('settings.restoreModalConfirmLabel')}</p>
                            <input className="form-control" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="RESTORE" autoFocus />
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                                <button type="button" className="btn btn-light" onClick={() => setRestoreTarget(null)}>{t('settings.cancel')}</button>
                                <button type="button" className="btn btn-danger" disabled={confirmText.trim().toUpperCase() !== 'RESTORE' || busyId === restoreTarget.id}
                                    onClick={runRestore} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {busyId === restoreTarget.id ? <><span className="spinner-border spinner-border-sm me-2" />{t('settings.restoring')}</> : t('settings.restoreNow')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Settings() {
    const { t } = useLanguage();
    const [form, setForm] = useState({
        system_name: '', org_name: '', contact_email: '', logo_url: '',
        currency: 'RWF', timezone: 'UTC',
        smtp_host: '', smtp_port: '587', smtp_user: '', smtp_password: '', smtp_from: '',
        // These start blank on purpose (not pre-filled with suggested copy):
        // the field's `placeholder` below shows sample text as a hint, but
        // an admin who saves without touching the field now genuinely saves
        // nothing, instead of silently publishing that sample text to the
        // live site as if it were real content they chose.
        homepage_hero_title: '', homepage_hero_subtitle: '',
        homepage_hero_title_fr: '', homepage_hero_title_rw: '',
        homepage_hero_subtitle_fr: '', homepage_hero_subtitle_rw: '',
        homepage_hero_description: '',
        homepage_hero_description_fr: '', homepage_hero_description_rw: '',
        homepage_about: '',
        homepage_about_fr: '', homepage_about_rw: '',
        homepage_phone: '', homepage_address: '', homepage_contact_email: '',
        homepage_hours: '', homepage_stat_rating: '', homepage_stat_availability: '',
        homepage_slide_1_title: '', homepage_slide_1_image: '',
        homepage_slide_1_title_fr: '', homepage_slide_1_title_rw: '',
        homepage_partner_1_name: '', homepage_partner_1_logo: '',
        homepage_partner_2_name: '', homepage_partner_2_logo: '',
        homepage_partner_3_name: '', homepage_partner_3_logo: '',
        homepage_partner_4_name: '', homepage_partner_4_logo: '',
        homepage_partner_5_name: '', homepage_partner_5_logo: '',
        homepage_partner_6_name: '', homepage_partner_6_logo: '',
        homepage_social_instagram: '', homepage_social_x: '', homepage_social_tiktok: '',
        homepage_social_linkedin: '', homepage_social_facebook: '', homepage_social_youtube: '',
        homepage_privacy_policy: '', homepage_terms_of_service: '',
        homepage_privacy_policy_fr: '', homepage_privacy_policy_rw: '',
        homepage_terms_of_service_fr: '', homepage_terms_of_service_rw: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [testing, setTesting] = useState(false);
    const [saved, setSaved] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [dataResource, setDataResource] = useState('buildings');
    const [dataFile, setDataFile] = useState(null);
    const [dataMessage, setDataMessage] = useState('');

    useEffect(() => {
        API.get('/settings').then(r => {
            setForm(prev => ({ ...prev, ...r.data }));
        }).finally(() => setLoading(false));
    }, []);

    const handleSave = async e => {
        e.preventDefault(); setSaving(true); setSaved(false);
        try {
            await API.post('/settings', form);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            alert(err.response?.data?.error || err.response?.data?.message || `${translateUiText('Save failed')}${err.response?.status ? ` (${err.response.status})` : ''}`);
        } finally { setSaving(false); }
    };

    const handleTestEmail = async () => {
        if (!testEmail) return;
        setTesting(true); setTestResult(null);
        try {
            const { data } = await API.post('/settings/test-email', { to_email: testEmail });
            setTestResult({ success: true, message: data.message });
        } catch (err) {
            setTestResult({ success: false, message: err.response?.data?.message || t('settings.testFailed') });
        } finally { setTesting(false); }
    };

    const f = (key) => ({
        value: form[key] || '',
        onChange: e => setForm({ ...form, [key]: e.target.value })
    });

    // Content the public site shows as-is (hero text, About, slide titles,
    // Privacy Policy, Terms of Service) can't go through the automatic UI
    // dictionary translation — it's free text you write, not a fixed label.
    // So each of these fields gets its own English/French/Kinyarwanda box.
    // Leaving French/Kinyarwanda blank doesn't mean visitors see English
    // there, though — the site auto-translates the English text for you
    // and shows that. Type your own text in a box only when you want to
    // override the automatic translation with your own wording.
    const LocalizedField = ({ base, label, textarea, rows = 2, placeholder, hint }) => (
        <div className="col-12">
            <label className="form-label fw-semibold">{label}</label>
            {hint && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>{hint}</div>}
            <div className="row g-2">
                {[['', 'English'], ['_fr', 'French'], ['_rw', 'Kinyarwanda']].map(([suffix, langLabel]) => (
                    <div className="col-md-4" key={suffix || 'en'}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3 }}>{langLabel}</div>
                        {textarea
                            ? <textarea className="form-control" rows={rows} {...f(`${base}${suffix}`)} placeholder={suffix ? 'Auto-translated — type here to override' : placeholder} />
                            : <input className="form-control" {...f(`${base}${suffix}`)} placeholder={suffix ? 'Auto-translated — type here to override' : placeholder} />}
                    </div>
                ))}
            </div>
        </div>
    );

    const handleSlideImage = (key, event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            alert(translateUiText('Please select an image file.'));
            event.target.value = '';
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert(translateUiText('Please select an image smaller than 5 MB.'));
            event.target.value = '';
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const image = new Image();
            image.onload = () => {
                const maxDimension = key === 'logo_url' ? 800 : key.includes('_logo') ? 400 : 1400;
                const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
                const canvas = document.createElement('canvas');
                canvas.width = Math.max(1, Math.round(image.width * scale));
                canvas.height = Math.max(1, Math.round(image.height * scale));
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#ffffff'; // JPEG has no transparency — keep transparent PNG areas white, not black
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
                const compressed = canvas.toDataURL('image/jpeg', 0.9);
                setForm(current => ({ ...current, [key]: compressed }));
            };
            image.src = reader.result;
        };
        reader.readAsDataURL(file);
    };

    const handleSlideVideo = async (number, event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('video/')) {
            alert(translateUiText('Please select a video file.'));
            event.target.value = '';
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            alert(translateUiText('Please select a video smaller than 20 MB.'));
            event.target.value = '';
            return;
        }
        const body = new FormData();
        body.append('video', file);
        body.append('slide', String(number));
        try {
            const { data } = await API.post('/settings/homepage-video', body);
            setForm(current => ({ ...current, [`homepage_slide_${number}_video`]: data.url }));
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Video upload failed.'));
        } finally {
            event.target.value = '';
        }
    };

    // Removing a video now acts immediately too, instead of just clearing
    // local form state and waiting for the next generic Save to notice --
    // that gap was exactly what let a stale Save silently undo an upload
    // (or an unrelated Save silently re-delete an already-removed video).
    const [removingVideoSlide, setRemovingVideoSlide] = useState(null);
    const removeSlideVideo = async number => {
        if (!window.confirm(translateUiText('Remove this slide video?'))) return;
        setRemovingVideoSlide(number);
        try {
            await API.delete(`/settings/homepage-video/${number}`);
            setForm(current => ({ ...current, [`homepage_slide_${number}_video`]: '' }));
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Could not remove video.'));
        } finally {
            setRemovingVideoSlide(null);
        }
    };

    const exportData = async () => {
        try {
            const response = await API.get(`/data/export/${dataResource}`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a'); link.href = url; link.download = `${dataResource}.csv`; link.click(); URL.revokeObjectURL(url);
            setDataMessage(t('settings.exportSuccess', '', { resource: dataResource }));
        } catch (err) { setDataMessage(err.response?.data?.error || t('settings.exportFailed')); }
    };

    const importData = async event => {
        event.preventDefault();
        if (!dataFile) return;
        const formData = new FormData(); formData.append('file', dataFile);
        try {
            const { data } = await API.post(`/data/import/${dataResource}`, formData);
            setDataMessage(data.message); setDataFile(null); event.target.reset();
        } catch (err) { setDataMessage(err.response?.data?.error || t('settings.importFailed')); }
    };

    if (loading) return <div className="spinner-wrap"><div className="spinner-border" /></div>;

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h4 style={{ margin: 0, fontWeight: 700 }}>{t('settings.pageTitle')}</h4>
                <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>{t('settings.pageSubtitle')}</p>
            </div>

            <form onSubmit={handleSave}>
                {/* General */}
                <Section icon={<MdBusiness style={{ color: '#0ea5e9' }} />} title={t('settings.sectionGeneral')}>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">{t('settings.systemNameLabel')}</label>
                            <input className="form-control" {...f('system_name')} placeholder="MITI FMS" />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">{t('settings.orgNameLabel')}</label>
                            <input className="form-control" {...f('org_name')} placeholder={t('settings.orgNamePlaceholder')} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">{t('settings.contactEmailLabel')}</label>
                            <input type="email" className="form-control" {...f('contact_email')} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">{t('settings.notifLogoLabel')}</label>
                            <input type="file" className="form-control" accept="image/*" onChange={event => handleSlideImage('logo_url', event)} />
                            {form.logo_url && <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                <img src={form.logo_url} alt="Notification logo preview" style={{ width: 120, height: 64, objectFit: 'contain', borderRadius: 6, border: '1px solid #e2e8f0', padding: 4 }} />
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setForm(current => ({ ...current, logo_url: '' }))}>{t('settings.removeLogo')}</button>
                            </div>}
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-semibold">{t('settings.currencyLabel')}</label>
                            <select className="form-select" {...f('currency')}>
                                {['RWF', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'KES', 'NGN', 'ZAR'].map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-semibold">{t('settings.timezoneLabel')}</label>
                            <select className="form-select" {...f('timezone')}>
                                {['UTC', 'Africa/Nairobi', 'Africa/Lagos', 'Africa/Johannesburg', 'Asia/Dubai',
                                    'Europe/London', 'America/New_York', 'America/Los_Angeles'].map(tz => (
                                        <option key={tz} value={tz}>{tz}</option>
                                    ))}
                            </select>
                        </div>
                    </div>
                </Section>

                <Section icon={<MdBusiness style={{ color: '#0ea5e9' }} />} title={t('settings.sectionHomepage')}>
                    <div className="row g-3">
                        <LocalizedField base="homepage_hero_title" label={t('settings.heroTitleLabel')} placeholder={t('settings.heroTitlePlaceholder')} />
                        <LocalizedField base="homepage_hero_subtitle" label={t('settings.heroSubtitleLabel')} placeholder={t('settings.heroSubtitlePlaceholder')} />
                        <LocalizedField base="homepage_hero_description" label={t('settings.heroDescLabel')} textarea rows={2} placeholder="Conference rooms, training halls, offices and more..."
                            hint={t('settings.heroDescHint')} />
                        <LocalizedField base="homepage_about" label={t('settings.aboutLabel')} textarea rows={3} placeholder={t('settings.aboutPlaceholder')} />
                        <div className="col-md-4"><label className="form-label fw-semibold">{t('settings.phoneLabel')}</label><input className="form-control" {...f('homepage_phone')} placeholder="+250 7XX XXX XXX" /></div>
                        <div className="col-md-4"><label className="form-label fw-semibold">{t('settings.addressLabel')}</label><input className="form-control" {...f('homepage_address')} placeholder="Kigali, Rwanda" /></div>
                        <div className="col-md-4"><label className="form-label fw-semibold">{t('settings.publicEmailLabel')}</label><input type="email" className="form-control" {...f('homepage_contact_email')} placeholder="info@example.com" /></div>
                        <div className="col-md-4"><label className="form-label fw-semibold">{t('settings.hoursLabel')}</label><input className="form-control" {...f('homepage_hours')} placeholder="Mon–Fri, 8am–6pm" /></div>
                        <div className="col-md-4"><label className="form-label fw-semibold">{t('settings.ratingStatLabel')}</label><input className="form-control" {...f('homepage_stat_rating')} placeholder="4.9" /><div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t('settings.ratingStatHint')}</div></div>
                        <div className="col-md-4"><label className="form-label fw-semibold">{t('settings.availabilityStatLabel')}</label><input className="form-control" {...f('homepage_stat_availability')} placeholder="24/7" /><div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t('settings.availabilityStatHint')}</div></div>
                        <LocalizedField base="homepage_slide_1_title" label={t('settings.slideTitleLabel')} />
                        <div className="col-12">
                            <label className="form-label fw-semibold">{t('settings.slideImageLabel')}</label>
                            <input type="file" className="form-control" accept="image/*" onChange={event => handleSlideImage('homepage_slide_1_image', event)} />
                            {form.homepage_slide_1_image && <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                <img src={form.homepage_slide_1_image} alt="Hero slide preview" style={{ width: 120, height: 64, objectFit: 'cover', borderRadius: 6 }} />
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setForm(current => ({ ...current, homepage_slide_1_image: '' }))}>{t('settings.removeImage')}</button>
                            </div>}
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold">{t('settings.slideVideoLabel')}</label>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>{t('settings.slideVideoHint')}</div>
                            <input type="file" className="form-control" accept="video/*" onChange={event => handleSlideVideo(1, event)} />
                            {form.homepage_slide_1_video && <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                <video src={form.homepage_slide_1_video} muted style={{ width: 120, height: 64, objectFit: 'cover', borderRadius: 6, background: '#000' }} />
                                <button type="button" className="btn btn-outline-danger btn-sm" disabled={removingVideoSlide === 1} onClick={() => removeSlideVideo(1)}>{removingVideoSlide === 1 ? t('settings.removingVideo') : t('settings.removeVideo')}</button>
                            </div>}
                        </div>
                        <div className="col-12"><hr /><h6 className="fw-semibold mb-0">{t('settings.partnersHeading')}</h6><div style={{ fontSize: 12, color: '#94a3b8' }}>{t('settings.partnersHint')}</div></div>
                        {[1, 2, 3, 4, 5, 6].map(number => <React.Fragment key={number}>
                            <div className="col-md-6"><label className="form-label fw-semibold">{t('settings.partnerNameLabel', '', { n: number })}</label><input className="form-control" {...f(`homepage_partner_${number}_name`)} placeholder={t('settings.partnerNamePlaceholder')} /></div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('settings.partnerLogoLabel', '', { n: number })}</label>
                                <input type="file" className="form-control" accept="image/*" onChange={event => handleSlideImage(`homepage_partner_${number}_logo`, event)} />
                                {form[`homepage_partner_${number}_logo`] && <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                    <img src={form[`homepage_partner_${number}_logo`]} alt={`Partner ${number} logo preview`} style={{ width: 80, height: 48, objectFit: 'contain', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0' }} />
                                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setForm(current => ({ ...current, [`homepage_partner_${number}_logo`]: '' }))}>{t('settings.removeLogo')}</button>
                                </div>}
                            </div>
                        </React.Fragment>)}
                        <div className="col-12"><hr /><h6 className="fw-semibold mb-0">{t('settings.socialHeading')}</h6></div>
                        {[
                            ['instagram', 'Instagram'], ['x', 'X'], ['tiktok', 'TikTok'],
                            ['linkedin', 'LinkedIn'], ['facebook', 'Facebook'], ['youtube', 'YouTube']
                        ].map(([key, label]) => <div className="col-md-6" key={key}>
                            <label className="form-label fw-semibold">{label} URL</label>
                            <input type="url" className="form-control" {...f(`homepage_social_${key}`)} placeholder={`https://${key === 'x' ? 'x.com' : `${key}.com`}/...`} />
                        </div>)}
                        <div className="col-12"><hr /><h6 className="fw-semibold mb-0">{t('settings.legalHeading')}</h6></div>
                        <LocalizedField base="homepage_privacy_policy" label={t('settings.privacyLabel')} textarea rows={5} placeholder={t('settings.privacyPlaceholder')} />
                        <LocalizedField base="homepage_terms_of_service" label={t('settings.termsLabel')} textarea rows={5} placeholder={t('settings.termsPlaceholder')} />
                    </div>
                </Section>

                {/* SMTP */}
                <Section icon={<MdEmail style={{ color: '#0ea5e9' }} />} title={t('settings.sectionSmtp')}>
                    <div style={{ background: '#fef9c3', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#854d0e', marginBottom: 16 }}>
                        {t('settings.smtpBanner')}
                    </div>
                    <div style={{ background: '#f0f9ff', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#075985', marginBottom: 16 }}>
                        {t('settings.smtpTrust')}
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">{t('settings.smtpHostLabel')}</label>
                            <input className="form-control" {...f('smtp_host')} placeholder="smtp.gmail.com" />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-semibold">{t('settings.smtpPortLabel')}</label>
                            <input type="number" className="form-control" {...f('smtp_port')} placeholder="587" />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-semibold">{t('settings.smtpFromLabel')}</label>
                            <input type="email" className="form-control" {...f('smtp_from')} placeholder="noreply@yourorg.com" />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">{t('settings.smtpUserLabel')}</label>
                            <input className="form-control" {...f('smtp_user')} placeholder="your@email.com" />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">{t('settings.smtpPasswordLabel')}</label>
                            <input type="password" className="form-control" {...f('smtp_password')} placeholder="App password or SMTP password" />
                        </div>
                    </div>

                    {/* Test email */}
                    <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 20, paddingTop: 20 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <MdSend style={{ color: '#0ea5e9' }} /> {t('settings.testEmailHeading')}
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <input type="email" className="form-control" placeholder={t('settings.testEmailPlaceholder')} value={testEmail}
                                onChange={e => setTestEmail(e.target.value)} style={{ maxWidth: 300 }} />
                            <button type="button" className="btn btn-light" onClick={handleTestEmail} disabled={testing || !testEmail}>
                                {testing ? <><span className="spinner-border spinner-border-sm me-2" />{t('settings.sendingTest')}</> : t('settings.sendTest')}
                            </button>
                        </div>
                        {testResult && (
                            <div style={{
                                marginTop: 10, padding: '8px 14px', borderRadius: 8, fontSize: 13,
                                background: testResult.success ? '#dcfce7' : '#fee2e2',
                                color: testResult.success ? '#15803d' : '#b91c1c'
                            }}>
                                {testResult.success ? '✅' : '❌'} {testResult.message}
                            </div>
                        )}
                    </div>
                </Section>

                <Section icon={<MdDownload style={{ color: '#0ea5e9' }} />} title={t('settings.sectionDataIO')}>
                    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
                        {t('settings.dataIoIntro')}
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <select className="form-select" style={{ maxWidth: 230 }} value={dataResource} onChange={e => setDataResource(e.target.value)}>
                            {DATA_RESOURCES.map(resource => <option key={resource} value={resource}>{resource.replaceAll('_', ' ')}</option>)}
                        </select>
                        <button type="button" className="btn btn-light" onClick={exportData}><MdDownload /> {t('settings.exportCsv')}</button>
                        <form onSubmit={importData} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="file" accept=".csv" onChange={e => setDataFile(e.target.files[0] || null)} />
                            <button type="submit" className="btn btn-primary" disabled={!dataFile || !IMPORT_RESOURCES.includes(dataResource)}><MdUpload /> {t('settings.importCsv')}</button>
                        </form>
                    </div>
                    {dataMessage && <div className="alert alert-info py-2 mt-3 mb-0" style={{ fontSize: 13 }}>{dataMessage}</div>}
                </Section>

                <Section icon={<MdBackup style={{ color: '#0ea5e9' }} />} title={t('settings.sectionBackup')}>
                    <BackupRestoreSection />
                </Section>

                {/* Security note */}
                <Section icon={<MdLock style={{ color: '#0ea5e9' }} />} title={t('settings.sectionSecurity')}>
                    <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.7 }}>
                        <p>{t('settings.securityIntro')}</p>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li><strong>{t('role.admin')}</strong> — {t('settings.roleAdminDesc')}</li>
                            <li><strong>{t('role.manager')}</strong> — {t('settings.roleManagerDesc')}</li>
                            <li><strong>Technician</strong> — {t('settings.roleTechnicianDesc')}</li>
                            <li><strong>Staff</strong> — {t('settings.roleStaffDesc')}</li>
                        </ul>
                        <p style={{ marginTop: 12, marginBottom: 0 }}>{t('settings.newUserPasswordNote')}</p>
                    </div>
                </Section>

                {/* Save */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                    {saved && (
                        <span style={{ fontSize: 13, color: '#15803d', fontWeight: 500 }}>{t('settings.savedBadge')}</span>
                    )}
                    <button type="submit" className="btn btn-primary" disabled={saving}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 24px' }}>
                        {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('settings.saving')}</> : <><MdSave size={16} />{t('settings.saveSettings')}</>}
                    </button>
                </div>
            </form>
        </div>
    );
}
